import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import img from "./all_bg.jpg";

const BACKEND_URL = "https://api.bbsfacultyprofile.com";
const TABLE_NAME = "editorial_service";

const EditorialService = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [formData, setFormData] = useState({
    year_from: null,
    year_upto: null,
    name: "",
    category: "",
    editorial_role: "",
    scopus: "",
    country: "",
  });
  const [adhocYear, setAdhocYear] = useState(null);

  const [recordId, setRecordId] = useState(null);

  const fetchRecords = async () => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/${TABLE_NAME}`, {
      method: "GET",
      credentials: "include",
    });

    if (!res.ok) throw new Error("Failed to fetch records");

    const data = await res.json();

    const processed = (Array.isArray(data) ? data : [])
      .map((r) => ({
        ...r,
        year_from: r.year_from ? new Date(r.year_from) : null,
        year_upto: r.year_upto ? new Date(r.year_upto) : null,
      }))
      .sort((a, b) => {
        if (!a.year_from) return 1;
        if (!b.year_from) return -1;

        return (
          b.year_from.getTime() -
          a.year_from.getTime()
        );
      });

    setRecords(processed);

  } catch (err) {
    console.error(err);
    toast.error(`Fetch error: ${err.message}`);
  }
};

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        year_from: formData.year_from
          ? `${formData.year_from.getFullYear()}-${String(
              formData.year_from.getMonth() + 1
            ).padStart(2, "0")}-01`
          : null,

        year_upto: formData.year_upto
          ? `${formData.year_upto.getFullYear()}-${String(
              formData.year_upto.getMonth() + 1
            ).padStart(2, "0")}-01`
          : null,
      };

      const url = recordId
        ? `${BACKEND_URL}/api/${TABLE_NAME}/${recordId}`
        : `${BACKEND_URL}/api/${TABLE_NAME}`;
      const method = recordId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to save record");

      toast.success("Editorial service saved successfully!");
      setFormData({
        year_from: null,
        year_upto: null,
        name: "",
        category: "",
        editorial_role: "",
        scopus: "",
        country: "",
      });
      setRecordId(null);
      setAdhocYear(null);

      fetchRecords();
    } catch (err) {
      console.error(err);
      toast.error(`Submit error: ${err.message}`);
    }
  };

  const handleEdit = (record) => {
    setFormData({
      year_from: record.year_from,
      year_upto: record.year_upto,
      name: record.name || "",
      category: record.category || "",
      editorial_role: record.editorial_role || "",
      scopus: record.scopus || "",
      country: record.country || "",
    });
    if (record.editorial_role === "ad-hoc reviewer") {
      setAdhocYear(record.year_from);
    }

    setRecordId(record.id);
    toast.info("Editing record...");
  };

  const handleDelete = (record) => {
    const toastId = toast.info(
      <div>
        Delete this record?{" "}
        <button
          onClick={async () => {
            try {
              const res = await fetch(
                `${BACKEND_URL}/api/${TABLE_NAME}/${record.id}`,
                { method: "DELETE", credentials: "include" }
              );
              if (!res.ok) throw new Error("Failed to delete record");
              fetchRecords();
              toast.update(toastId, {
                render: "Record deleted successfully!",
                type: "success",
                autoClose: 3000,
                closeButton: true,
              });
            } catch (err) {
              toast.update(toastId, {
                render: `Delete error: ${err.message}`,
                type: "error",
                autoClose: 3000,
                closeButton: true,
              });
            }
          }}
          style={{
            marginLeft: "10px",
            padding: "3px 8px",
            background: "#ef4444",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Confirm
        </button>
      </div>,
      { autoClose: false, closeOnClick: false }
    );
  };

  const formatMonthYear = (date) => {
    if (!date) return "";
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${year}`;
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        fontFamily: "Arial, sans-serif",
        backgroundImage: `url(${img})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <ToastContainer position="top-center" autoClose={3000} />
      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          background: "#fff",
          padding: "30px 40px",
          borderRadius: "10px",
          boxShadow: "0px 0px 12px rgba(0,0,0,0.1)",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            color: "#1e3a8a",
            marginBottom: "25px",
          }}
        >
          Editorial Service
        </h2>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column" }}
        >
          {/* Editorial Role Dropdown */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Role
            </label>
            <select
              name="editorial_role"
              value={formData.editorial_role}
              onChange={handleChange}
              style={{
                padding: "10px",
                marginTop: "5px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                width: "100%",
                marginBottom: "5px",
              }}
              required
            >
              <option value="">Select...</option>
              <option value="editor">Editor-in-Chief</option>
              <option value="associate-editor">Associate Editor</option>
              <option value="special issue editor">Special Issue Editor</option>
              <option value="editorial board member">
                Editorial Board Member
              </option>
              <option value="ad-hoc reviewer">Ad-hoc Reviewer</option>
            </select>
          </div>

          {/* Dates Row */}
          {formData.editorial_role === "ad-hoc reviewer" ? (
            <div>
              <label style={{ fontWeight: "bold" }}>Year</label>
              <DatePicker
                selected={adhocYear}
                onChange={(date) => {
                  setAdhocYear(date);
                  setFormData({
                    ...formData,
                    year_from: date,
                    year_upto: date,
                  });
                }}
                dateFormat="MM/yyyy"
                showMonthYearPicker
                placeholderText="Select month/year"
              />
            </div>
          ) : (
            <div style={{ display: "flex", gap: "10px" }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontWeight: "bold" }}>Starting Year</label>
                <DatePicker
                  selected={formData.year_from}
                  onChange={(date) =>
                    setFormData({ ...formData, year_from: date })
                  }
                  dateFormat="MM/yyyy"
                  showMonthYearPicker
                  placeholderText="Select month/year"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontWeight: "bold" }}>Ending Year</label>
                <DatePicker
                  selected={formData.year_upto}
                  onChange={(date) =>
                    setFormData({ ...formData, year_upto: date })
                  }
                  dateFormat="MM/yyyy"
                  showMonthYearPicker
                  placeholderText="Select month/year"
                />
              </div>
            </div>
          )}

          {/* Category Dropdown */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Category
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              style={{
                padding: "10px",
                marginTop: "5px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                width: "100%",
              }}
              required
            >
              <option value="">Select...</option>
              <option value="Academic Journal"> Academic Journal</option>
              <option value="conference"> Conference</option>

              <option value="book">Edited Book</option>
              <option value="book chapter"> Book Chapter</option>
              <option value="cases"> Case</option>
              <option value="trade journal"> Trade Journal</option>
              <option value="grant proposal"> Grant/Grant Proposal</option>
            </select>
          </div>

          {/* Name */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Outlet Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              style={{
                padding: "10px",
                marginTop: "5px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                width: "100%",
              }}
              required
            />
          </div>

          {/* Scopus Dropdown */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Scopus Index
            </label>
            <select
              name="scopus"
              value={formData.scopus}
              onChange={handleChange}
              style={{
                padding: "10px",
                marginTop: "5px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                width: "100%",
              }}
              required
            >
              <option value="">Select...</option>
              <option value="Q1: Top 10% (90th percentile or above">
                Q1: Top 10% (90ᵗʰ percentile or above)
              </option>
              <option value="Q1: 75th to 89th percentile">
                Q1: 75th to 89ᵗʰ percentile
              </option>
              <option value="Q2: 50th to 74th percentile">
                Q2: 50ᵗʰ to 74ᵗʰ percentile
              </option>
              <option value="Q3: 25th to 49th percentile">
                Q3: 25ᵗʰ to 49ᵗʰ percentile
              </option>
              <option value="Q4: 0 to 24th percentile">
                Q4: 0 to 24ᵗʰ percentile
              </option>
              <option value="Not Applicable">Not Applicable</option>
            </select>
          </div>

          {/* Country */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Country
            </label>
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleChange}
              style={{
                padding: "10px",
                marginTop: "5px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                width: "100%",
              }}
            />
          </div>

          <button
            type="submit"
            style={{
              background: "#3b82f6",
              color: "white",
              padding: "12px",
              border: "none",
              borderRadius: "6px",
              marginTop: "20px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {recordId ? "Update Record" : "Add Record"}
          </button>
        </form>

        {/* Records Table */}
        {records.length > 0 && (
          <div style={{ marginTop: "30px", overflowX: "auto" }}>
            <h3 style={{ color: "#1e3a8a" }}>Your Records</h3>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "10px",
              }}
            >
              <thead>
                <tr>
                  {[
                    "Year From",
                    "Year Upto",
                    "Outlet Name",
                    "Category",
                    "Role",
                    "Scopus",
                    "Country",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        background: "#f1f5f9",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((rec) => (
                  <tr key={rec.id}>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {formatMonthYear(rec.year_from)}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {formatMonthYear(rec.year_upto)}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.name}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.category}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.editorial_role}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.scopus}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.country}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        textAlign: "center",
                      }}
                    >
                      <div style={{ display: "inline-flex", gap: "5px" }}>
                        <button
                          onClick={() => handleEdit(rec)}
                          style={{
                            background: "#2563eb",
                            color: "white",
                            border: "none",
                            borderRadius: "5px",
                            padding: "5px 10px",
                            cursor: "pointer",
                          }}
                        >
                          Edit
                        </button>

                        <button
                          onClick={() => handleDelete(rec)}
                          style={{
                            background: "#ef4444",
                            color: "white",
                            border: "none",
                            borderRadius: "5px",
                            padding: "5px 10px",
                            cursor: "pointer",
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "25px" }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "#1e3a8a",
              color: "white",
              padding: "10px 20px",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditorialService;
